import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from '@reduxjs/toolkit';
import { authApi } from './api/authApi';
import { userApi } from './api/userApi';
import { twilioApi } from './api/twilioApi';
import { emailApi } from './api/emailApi';
import { autoSyncApi } from './api/autoSyncApi';
import { jobRunsApi } from './api/jobRunsApi';
import { synthflowApi } from './api/synthflowApi';
import { synthflowAgentsApi } from './api/synthflowAgentsApi';
import { emailAddressesApi } from './api/emailAddressesApi';
import { userEmailCredentialsApi } from './api/userEmailCredentialsApi';
import { webhooksApi } from './api/webhooksApi';
import { outboundCallingApi } from './api/outboundCallingApi';
import authReducer from './slices/authSlice';

// Configure persist for auth slice only
const persistConfig = {
  key: 'auth',
  storage,
  whitelist: ['token', 'isAuthenticated', 'user'], // Only persist these fields
};

const persistedAuthReducer = persistReducer(persistConfig, authReducer);

const rootReducer = combineReducers({
  auth: persistedAuthReducer,
  [authApi.reducerPath]: authApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [twilioApi.reducerPath]: twilioApi.reducer,
  [emailApi.reducerPath]: emailApi.reducer,
  [autoSyncApi.reducerPath]: autoSyncApi.reducer,
  [jobRunsApi.reducerPath]: jobRunsApi.reducer,
  [synthflowApi.reducerPath]: synthflowApi.reducer,
  [synthflowAgentsApi.reducerPath]: synthflowAgentsApi.reducer,
  [emailAddressesApi.reducerPath]: emailAddressesApi.reducer,
  [userEmailCredentialsApi.reducerPath]: userEmailCredentialsApi.reducer,
  [webhooksApi.reducerPath]: webhooksApi.reducer,
  [outboundCallingApi.reducerPath]: outboundCallingApi.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    })
      .concat(authApi.middleware)
      .concat(userApi.middleware)
      .concat(twilioApi.middleware)
      .concat(emailApi.middleware)
      .concat(autoSyncApi.middleware)
      .concat(jobRunsApi.middleware)
      .concat(synthflowApi.middleware)
      .concat(synthflowAgentsApi.middleware)
      .concat(emailAddressesApi.middleware)
      .concat(userEmailCredentialsApi.middleware)
      .concat(webhooksApi.middleware)
      .concat(outboundCallingApi.middleware),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
